import { ActivatedRoute, Router, RouterModule } from "@angular/router";
import { Component, ViewChild, HostListener, AfterViewInit , CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA} from "@angular/core";
import { CommonModule } from "@angular/common";
import { FormsModule, ReactiveFormsModule } from "@angular/forms";
import { FormGroup, FormControl, Validators } from "@angular/forms";
import { ToastrService } from "ngx-toastr";
import { IDropDownItemViewEdit, IDropDownView } from "../../interfaces/idropdowns";
import { ICountry } from "../../interfaces/icountry";
import { DropDownItem } from "../../models/dropdown";
import { DropDownItemService } from "../../services/dropdownitem.service";
import { ISelectItem } from "../../interfaces/iselectitem";
import { ICanComponentDeactivate } from "../../interfaces/icancomponentdeactivate";
import { Observable } from "rxjs";
import { ConfirmationModalComponent } from "../confirmationmodal/confirmationmodal.component";
import { HeaderComponent } from "../header/header.component";

/**
 * DropDownItem Component
 */
@Component({
    selector: "dropdownprepopulateitems",
    templateUrl: "./dropdownprepopulateitems.component.html",
    standalone: true,
    imports: [CommonModule, FormsModule, ReactiveFormsModule, RouterModule, ConfirmationModalComponent, HeaderComponent],
    schemas: [CUSTOM_ELEMENTS_SCHEMA, NO_ERRORS_SCHEMA]
})
export class DropDownPrepopulateItemsComponent implements ICanComponentDeactivate, AfterViewInit {
    dropDown: IDropDownView;
    dropDownItem: IDropDownItemViewEdit;
    countries: ICountry[];
    listOptions: DropDownItem[];
    filteredListOptions: DropDownItem[];
    selectionOptions: ISelectItem[];
    selectedPredefinedList: DropDownItem = null;
    dropDownPrepopulateForm: FormGroup;
    originalData: string;
    selectAll: boolean = true;
    showDropdown: boolean = false;
    searchTerm: string = '';
    showValidationError: boolean = false;

    @ViewChild("confirmModal") confirmModal;
    /**
     * Initiates the form controls
     * @param activatedRoute Contains the selected drop down and colour palettes
     * @param dropDownItemService Used for all drop down item related operations
     * @param toastsManager Displays messages to the user
     * @param router Used for navigation
     */
    constructor(private activatedRoute: ActivatedRoute,
        private dropDownItemService: DropDownItemService,
        private toastsManager: ToastrService,
        private router: Router) {
        this.dropDown = activatedRoute.snapshot.data["dropDown"] as IDropDownView;

        this.listOptions = [new DropDownItem(1, "Countries", null)];
        this.filteredListOptions = [...this.listOptions];
        this.dropDownPrepopulateForm = new FormGroup({
            selections: new FormGroup({})
        });
    }

    ngAfterViewInit(): void {
        this.originalData = JSON.stringify(this.dropDownPrepopulateForm.value);
    }

    /**
     * Saves the values from the form
     * @param item The item to create/update
     * @param valid True if the form is valid, false if it is not
     */
    saveClicked(checkboxes: any): void {
        // Validate that a predefined list is selected
        if (!this.selectedPredefinedList) {
            this.showValidationError = true;
            this.toastsManager.error("Please select a predefined list before saving");
            return;
        }

        if (checkboxes && checkboxes.selections) {
            let selectedItems: ISelectItem[] = this.getSelectedItems(checkboxes.selections);
            this.dropDownItemService.createMultiple(this.dropDown.id, selectedItems).subscribe(() => {
                this.toastsManager.success("Drop down items created");
                this.ngAfterViewInit();
                this.router.navigate(["/dropdown", this.dropDown.id]);
            });
        }
    }

    private getSelectedItems(items: any): ISelectItem[] {
        let selectedItems: ISelectItem[] = [];
        let checkBoxes: string[] = Object.keys(items);
        for (const key of checkBoxes) {
            if (items[key] === true) {
                let item: ISelectItem = this.selectionOptions.find(c => c.value === key);
                selectedItems.push(item);
            }
        }
        return selectedItems.sort((t1, t2) => {
            if (t1.text > t2.text) {
                return 1;
            }
            if (t1.text < t2.text) {
                return -1;
            }
            return 0;
        });
    }

    pdlistSelected(event: any): void {
        const selectedId = parseInt(event.target.value);
        if (selectedId) {
            this.selectedPredefinedList = this.listOptions.find(list => list.id === selectedId);
            this.loadListData(selectedId);
        } else {
            this.selectedPredefinedList = null;
            this.selectionOptions = [];
            this.dropDownPrepopulateForm = new FormGroup({});
        }
    }

    onSearchChange(searchValue: string): void {
        const searchTerm = searchValue.toLowerCase();
        if (searchTerm === '') {
            this.filteredListOptions = [...this.listOptions];
        } else {
            this.filteredListOptions = this.listOptions.filter(option => 
                option.text.toLowerCase().includes(searchTerm)
            );
        }
        this.showDropdown = true;
    }

    onInputFocus(): void {
        this.showDropdown = true;
        if (this.filteredListOptions.length === 0) {
            this.filteredListOptions = [...this.listOptions];
        }
    }

    onInputBlur(): void {
        // Small delay to allow click on dropdown items
        setTimeout(() => {
            if (!this.showDropdown) {
                // Restore search text to selected value or clear if no selection
                this.searchTerm = this.selectedPredefinedList?.text || '';
            }
        }, 150);
    }

    onKeyDown(event: KeyboardEvent): void {
        if (event.key === 'Escape') {
            this.showDropdown = false;
            // Restore search text to selected value or clear if no selection
            this.searchTerm = this.selectedPredefinedList?.text || '';
        }
    }

    selectOption(option: DropDownItem, event: Event): void {
        event.preventDefault();
        this.selectedPredefinedList = option;
        this.searchTerm = option.text;
        this.showDropdown = false;
        this.showValidationError = false; // Clear validation error when selection is made
        this.loadListData(option.id);
    }

    isFormValid(): boolean {
        return this.selectedPredefinedList !== null;
    }

    /**
     * Load all the country dashboard infromation.
     */
    loadListData(predefinedListId: number): void {
        this.dropDownItemService.getPredefinedList(predefinedListId).subscribe((data: ISelectItem[]) => {
            this.selectionOptions = data;
            data.forEach(item => {
                let selectionGroup: FormGroup = this.dropDownPrepopulateForm.controls["selections"] as FormGroup;
                selectionGroup.addControl(item.value, new FormControl(true));
            });
        });
    }

    private selectAllItems(): void {
        this.selectAll = !this.selectAll;
        let selectionGroup: FormGroup = this.dropDownPrepopulateForm.controls["selections"] as FormGroup;
        Object.keys(selectionGroup.controls).forEach(key => {
            selectionGroup.controls[key].setValue(this.selectAll);
        });
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(event: Event): void {
        const target = event.target as HTMLElement;
        if (!target.closest('.dropdown')) {
            this.showDropdown = false;
            // Restore search text to selected value or clear if no selection
            this.searchTerm = this.selectedPredefinedList?.text || '';
        }
    }

    @HostListener("window:beforeunload")
    canDeactivate(): Observable<boolean> | Promise<boolean> | boolean {
        let changedSet: string = JSON.stringify(this.dropDownPrepopulateForm.value);
        if (this.originalData !== changedSet) {
            this.confirmModal.openModal();
            return false;
        } else {
            return true;
        }
    }
    onAccept(): void {
        // reset the the original back to whats currenlty in itemConfigSet, this allows to pass check  in candeactivate
        this.originalData = JSON.stringify(this.dropDownPrepopulateForm.value);
        this.router.navigate([this.navigatingTo]);
        this.confirmModal.closeModal();
    }

    onDecline(): void {
        this.confirmModal.closeModal();
    }
    navigatingTo: string;
}




